import React, { useState, useEffect, useRef, memo, useCallback, useMemo } from 'react';
import userProfileAPI from '../api/userProfile';
import { safeGet, safeRemove } from '../utils/safeStorage';
import { STORAGE_KEYS } from '../constants/storageKeys';

const GSTSelector = memo(function GSTSelector({ onGSTChange, selectedGSTId, style = {}, variant = 'default' }) {
  const [gstOptions, setGstOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const onGSTChangeRef = useRef(onGSTChange);
  useEffect(() => { onGSTChangeRef.current = onGSTChange; }, [onGSTChange]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const options = await userProfileAPI.getGSTDropdownOptions();
        if (cancelled) return;
        setGstOptions(options);

        const savedGST = safeGet(STORAGE_KEYS.selectedGST);
        if (savedGST) {
          try {
            const gstData = JSON.parse(savedGST);
            if (options.find(option => option.value === gstData.value)) {
              return;
            }
            safeRemove(STORAGE_KEYS.selectedGST);
          } catch (e) {
            console.error('Error parsing saved GST:', e);
            safeRemove(STORAGE_KEYS.selectedGST);
          }
        }

        if (!selectedGSTId && options.length > 0) {
          onGSTChangeRef.current(options[0]);
        }
      } catch (err) {
        if (!cancelled) setError('Failed to load GST options');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedGSTId]);

  const handleGSTChange = useCallback((e) => {
    const gstId = parseInt(e.target.value);
    const selectedGST = gstOptions.find(option => option.value === gstId);
    if (selectedGST) {
      onGSTChange(selectedGST);
    }
  }, [gstOptions, onGSTChange]);

  const formatGSTDisplay = useCallback((gst) => {
    if (!gst) return gst;
    return gst; // Return GST number without dashes
  }, []);

  const selectStyles = useMemo(() => {
    const baseStyles = {
      width: "100%",
      padding: "8px 12px",
      borderRadius: "6px",
      fontSize: "14px",
      cursor: "pointer",
      ...style
    };

    if (variant === 'sidebar') {
      return {
        ...baseStyles,
        backgroundColor: "#ffffff !important",
        border: "1px solid #d1d5db",
        color: "#000000 !important",
        WebkitAppearance: "none",
        MozAppearance: "none",
        appearance: "none"
      };
    }
    if (variant === 'topbar') {
      return {
        ...baseStyles,
        width: "100%",
        minWidth: 0,
        maxWidth: "min(220px, calc(100vw - 200px))",
        padding: "6px 10px",
        fontSize: "13px",
        backgroundColor: "rgba(255,255,255,0.1)",
        border: "1px solid rgba(255,255,255,0.2)",
        color: "#f8fafc",
        WebkitAppearance: "none",
        MozAppearance: "none",
        appearance: "none"
      };
    }
    return {
      ...baseStyles,
      backgroundColor: "rgba(255,255,255,0.1)",
      border: "1px solid rgba(255,255,255,0.2)",
      color: "#f8fafc"
    };
  }, [style, variant]);

  if (loading) {
    return (
      <div style={{
        padding: "8px 12px",
        color: "#9ca3af",
        fontSize: "14px",
        ...style
      }}>
        Loading GST...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: "8px 12px",
        color: "#ef4444",
        fontSize: "14px",
        ...style
      }}>
        {error}
      </div>
    );
  }

  if (gstOptions.length === 0) {
    return (
      <div style={{
        padding: "8px 12px",
        color: "#9ca3af",
        fontSize: "14px",
        ...style
      }}>
        No GST numbers available
      </div>
    );
  }

  return (
    <select
      value={selectedGSTId || ''}
      onChange={handleGSTChange}
      className={variant === "topbar" ? "topbar-gst-select" : undefined}
      style={selectStyles}
    >
      {gstOptions.map((option) => (
        <option 
          key={option.value} 
          value={option.value}
          style={{
            color: variant === 'sidebar' ? '#000000' : '#f8fafc',
            backgroundColor: variant === 'sidebar' ? '#ffffff' : 'transparent'
          }}
        >
          {formatGSTDisplay(option.gst_number)} - {option.state_name}
        </option>
      ))}
    </select>
  );
});

export default GSTSelector;
